import { useContext, useEffect, useState } from "react";
import { endPoints, getData } from "../../utilities/networkCalls";
import { AppConfigContext } from "../../context/AppConfigContext";
import useBundleMixpanelHook from "../../hooks/useBundleMixpanelHook";
import styles from "./ExtensionAndEmbed.module.css";
import { Badge, Button, Link, Tooltip } from "@shopify/polaris";
import CalloutNudgeBanner from "../CalloutNudgeBanner";

const states = {
    enabled: 1,
    disabled: 0,
    loading: -1,
};

const BLOCKS_MAP = {
    bundle_builder_position: "Kefi Builder CTA",
    bxgy_position: "Kefi BXGY Bundle",
    fbt_position: "Kefi FBT",
    mnm_position: "Kefi Mix & Match",
    mnms_position: "Kefi M&M Sectional",
    product_add_on: "Kefi Product Add-on",
    volume_position: "Kefi Quantity Breaks",
    standard_position: "Kefi Standard Bundle",
    buy_n_position: "Kefi Buy-N Bundle",
    placeholder_position: "Kefi BB Placeholder",
};

const ExtensionAndEmbed = () => {
    const [isEnabled, setIsEnabled] = useState(states.loading);
    const [isLoading, setIsLoading] = useState(false);

    const [appBlocksCount, setAppBlocksCount] = useState(0);
    const [appBlocksList, setAppBlocksList] = useState<string[]>([]);

    const appConfigContext = useContext(AppConfigContext);
    const { trackEnableCoreSnippet } = useBundleMixpanelHook();
    const extensionUrl = `https://admin.shopify.com/store/${appConfigContext.shop}/themes/current/editor?context=apps&activateAppId=${appConfigContext.extensionId}%2Fkefi`;

    useEffect(() => {
        let isMounted = true;

        const checkExtension = async () => {
            try {
                setIsLoading(true);
                shopify.loading(true);

                const res = await getData({ url: endPoints.extensionStatus });
                if (!isMounted) return; // prevent state update after unmount

                shopify.loading(false);

                if (res.status === 200) {
                    setIsEnabled(res.data.status === 1 ? states.enabled : states.disabled);
                    setAppBlocksCount(res?.data?.appBlocksCount || 0);
                    setAppBlocksList(res?.data?.appBlocksList || []);
                } else {
                    setIsEnabled(states.disabled);
                }
            } catch (err) {
                if (isMounted) {
                    setIsEnabled(states.disabled);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        const handleVisibility = () => {
            void checkExtension();
        };

        void checkExtension();

        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            isMounted = false;
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, []);

    return (
        <div className={`${styles.wrapper} ${styles.gridWrapper}`}>
            <CalloutNudgeBanner onHomePage={true} />

            <div className={styles.singleBox}>
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                    }}
                >
                    <h4 className={styles.heading} style={{ fontWeight: "600" }}>
                        App Embed
                    </h4>
                    {isLoading ? (
                        <span style={{ width: "100px" }} className={styles.skeleton}></span>
                    ) : (
                        <Badge
                            tone={isEnabled == 1 ? "success" : "warning"}
                            children={isEnabled == 1 ? "Active" : "Inactive"}
                        />
                    )}
                </div>

                {isEnabled == 1 && <p>Your offers would be visible on your store</p>}
                {isEnabled == 0 && (
                    <>
                        <p>Offers will not be visible when app embed is inactive.</p>
                        <Link
                            onClick={() => {
                                trackEnableCoreSnippet({
                                    cta: "Activate App Embed",
                                    ctaType: "Banner Card",
                                });
                            }}
                            url={extensionUrl}
                            target="_blank"
                        >
                            <Button variant="primary">Activate App Embed</Button>
                        </Link>
                    </>
                )}
            </div>

            <div className={styles.singleBox}>
                <h4 className={styles.heading} style={{ fontWeight: "600" }}>
                    Active App Block(s)
                </h4>
                <p className={styles.content} style={{ whiteSpace: "nowrap" }}>
                    You have&nbsp;
                    {isLoading ? (
                        <span style={{ width: "55px" }} className={styles.skeleton}></span>
                    ) : (
                        <Tooltip
                            width="wide"
                            content={
                                <>
                                    {Object.entries(BLOCKS_MAP).map((b) => {
                                        const key = b[0];
                                        const val = b[1];

                                        const isInstalled = appBlocksList.some((name) =>
                                            name.toLowerCase().includes(key.toLowerCase()),
                                        );

                                        return (
                                            <p
                                                key={val}
                                                style={{
                                                    margin: "2px",
                                                }}
                                            >
                                                {val} -{" "}
                                                <Badge tone={isInstalled ? "success" : "info"} size="small">
                                                    {isInstalled ? "Installed" : "Not Installed"}
                                                </Badge>
                                            </p>
                                        );
                                    })}
                                </>
                            }
                        >
                            <span
                                style={{
                                    cursor: "pointer",
                                    borderBottom: "2px dotted #000000",
                                }}
                            >
                                {appBlocksCount} active
                            </span>
                        </Tooltip>
                    )}
                    &nbsp;app block(s) on your store.
                </p>
            </div>
        </div>
    );
};

export default ExtensionAndEmbed;
